import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** Query context excludes page, but includes filters, sort and locale. */
  #context = '';
  #generation = 0;
  #pendingPages = new Map();
  #renderingPages = new Set();

  /** @type {string | null} */
  #imageRatioSetting = null;

  connectedCallback() {
    super.connectedCallback();

    this.#storeImageRatioSettings();

    this.#resetPagination();
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.#contextKey() !== this.#context) {
      this.#resetPagination();
    } else {
      // Section morphs may replace a sentinel even when the query is unchanged.
      this.#observeViewMore();
    }
  }

  #contextKey(url = new URL(window.location.href)) {
    const key = new URL(url);
    key.searchParams.delete('page');
    key.searchParams.delete('section_id');
    key.searchParams.sort();
    key.hash = '';
    return key.toString();
  }

  #isCurrent(generation) {
    return this.isConnected && generation === this.#generation && this.#contextKey() === this.#context;
  }

  #resetPagination() {
    this.#generation += 1;
    this.#context = this.#contextKey();
    this.pages.clear();
    this.#pendingPages.clear();
    this.#renderingPages.clear();
    // Keep explicit product pagination stable instead of inserting cards above its links.
    if (this.#usesExplicitPagination()) {
      this.infinityScrollObserver?.disconnect();
      return;
    }
    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
  }

  /**
   * Store the image ratio from the first product card for later use
   */
  #storeImageRatioSettings() {
    const firstCardGallery = this.querySelector('[ref="cardGallery"]');
    if (!firstCardGallery) return;

    this.#imageRatioSetting = firstCardGallery.getAttribute('data-image-ratio');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#generation += 1;
    this.pages.clear();
    this.#pendingPages.clear();
    this.#renderingPages.clear();
    this.infinityScrollObserver?.disconnect();
  }

  #usesExplicitPagination() {
    return this.localName === 'results-list' && Boolean(this.querySelector('aurora-page-nav'));
  }

  #observeViewMore() {
    this.infinityScrollObserver?.disconnect();
    if (this.#usesExplicitPagination()) return;
    const { viewMorePrevious, viewMoreNext } = this.refs;

    if (!viewMorePrevious || !viewMoreNext) return;

    this.infinityScrollObserver = new IntersectionObserver(
      async (entries) => {
        const generation = this.#generation;
        // Wait for any in-progress view transitions to finish
        if (viewTransition.current) await viewTransition.current;
        if (!this.#isCurrent(generation)) return;

        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (entry.target === viewMorePrevious) {
              this.#renderPreviousPage();
            } else {
              this.#renderNextPage();
            }
          }
        }
      },
      {
        rootMargin: '100px',
      }
    );

    this.infinityScrollObserver.observe(viewMorePrevious);
    this.infinityScrollObserver.observe(viewMoreNext);
  }

  /**
   * @param {Object} pageInfo - The page info
   * @param {number} pageInfo.page - The page number
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;
    if (pageInfo.page > this.#getLastPage() || pageInfo.page < 1) return false;

    return true;
  }

  #getLastPage() {
    const { grid } = this.refs;

    if (!grid) return 0;

    return Number(grid.getAttribute('last-page'));
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);
    if (!page || !this.#shouldUsePage(page)) return;
    await this.#loadPage(page, this.#generation);
  }

  async #loadPage(page, generation) {
    if (!this.#isCurrent(generation)) return false;
    if (this.pages.has(page.page)) return true;
    const pending = this.#pendingPages.get(page.page);
    if (pending) return pending;

    const promise = (async () => {
      try {
        // SectionRenderer mutates its URL argument; keep history free of section_id.
        const html = await sectionRenderer.getSectionHTML(this.sectionId, true, new URL(page.url));
        if (!this.#isCurrent(generation)) return false;
        this.pages.set(page.page, html);
        return true;
      } catch (error) {
        if (this.#isCurrent(generation)) console.error('Unable to load product page', error);
        return false;
      } finally {
        if (this.#pendingPages.get(page.page) === promise) this.#pendingPages.delete(page.page);
      }
    })();
    this.#pendingPages.set(page.page, promise);
    return promise;
  }

  async #renderNextPage() {
    await this.#renderPage('next');
  }

  async #renderPreviousPage() {
    await this.#renderPage('previous');
  }

  async #renderPage(type) {
    const generation = this.#generation;
    if (!this.#isCurrent(generation)) return;
    const page = this.#getPage(type);
    if (!page || !this.#shouldUsePage(page)) return;
    const renderKey = `${generation}:${type}:${page.page}`;
    if (this.#renderingPages.has(renderKey)) return;
    this.#renderingPages.add(renderKey);

    try {
      if (!(await this.#loadPage(page, generation))) return;
      if (!this.#isCurrent(generation) || this.#getPage(type)?.page !== page.page) return;
      const grid = this.refs.grid;
      const elements = this.#getGridForPage(page.page);
      if (!grid || !elements) return;

      if (type === 'previous') {
        const scrollTop = window.scrollY;
        const firstElement = grid.firstElementChild;
        const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;
        grid.prepend(...elements);
        if (firstElement) {
          const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({ top: scrollTop + newHeight - oldHeight, behavior: 'instant' });
        }
      } else {
        grid.append(...elements);
      }

      this.#processNewElements();
      history.replaceState(history.state, '', page.url.toString());
      requestIdleCallback(() => {
        if (this.#isCurrent(generation)) this.#fetchPage(type);
      });
    } finally {
      this.#renderingPages.delete(renderKey);
    }
  }

  /**
   * Process newly added elements and apply correct aspect ratios
   */
  #processNewElements() {
    // Wait for the DOM to update
    requestAnimationFrame(() => {
      this.#imageRatioSetting === 'adapt' ? this.#fixAdaptiveAspectRatios() : this.#applyFixedAspectRatio();
    });
  }

  /**
   * Get all unprocessed card galleries
   * @returns {NodeListOf<Element>} List of unprocessed galleries
   */
  #getUnprocessedGalleries() {
    return this.querySelectorAll('.card-gallery:not([data-aspect-ratio-applied])');
  }

  /**
   * Mark gallery as processed
   * @param {HTMLElement} gallery - The gallery element to mark as processed
   */
  #markAsProcessed(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    gallery.setAttribute('data-aspect-ratio-applied', 'true');
  }

  /**
   * Calculate a safe aspect ratio value from image dimensions
   * Ensures the ratio stays within reasonable bounds and has consistent decimal places
   * @param {number} width - Natural width of the image
   * @param {number} height - Natural height of the image
   * @returns {string} Normalized aspect ratio as a string
   */
  #getSafeImageAspectRatio(width, height) {
    const rawRatio = width / height;
    return Math.max(0.1, Math.min(10, rawRatio)).toFixed(3);
  }

  /**
   * Apply an aspect ratio to a gallery and all its media containers
   * @param {HTMLElement} gallery - The gallery element
   * @param {string} aspectRatio - The aspect ratio to apply
   */
  #applyAspectRatioToGallery(gallery, aspectRatio) {
    if (!(gallery instanceof HTMLElement)) return;

    gallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);

    const mediaContainers = gallery.querySelectorAll('.product-media-container');
    mediaContainers.forEach((container) => {
      if (container instanceof HTMLElement) {
        container.style.aspectRatio = aspectRatio;
      }
    });

    this.#markAsProcessed(gallery);
  }

  /**
   * Fix adaptive aspect ratios for newly added cards
   * For the 'adapt' setting, each product should use its own image's aspect ratio
   */
  #fixAdaptiveAspectRatios() {
    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    const productRatioCache = new Map();

    newCardGalleries.forEach((gallery) => {
      if (!(gallery instanceof HTMLElement)) return;

      const productId = gallery.getAttribute('data-product-id');
      if (productId && productRatioCache.has(productId)) {
        this.#applyAspectRatioToGallery(gallery, productRatioCache.get(productId));
        return;
      }

      const img = gallery.querySelector('img');
      if (!img) {
        this.#applyAspectRatioToGallery(gallery, '1');
        return;
      }

      const loadAndSetRatio = () => {
        if (!img.naturalWidth || !img.naturalHeight) return;

        const imgRatio = this.#getSafeImageAspectRatio(img.naturalWidth, img.naturalHeight);

        if (productId) {
          productRatioCache.set(productId, imgRatio);
        }

        this.#applyAspectRatioToGallery(gallery, imgRatio);
      };

      if (img.complete) {
        loadAndSetRatio();
      } else {
        img.addEventListener('load', loadAndSetRatio, { once: true });
      }
    });
  }

  /**
   * Apply a fixed aspect ratio to all card-gallery and media container elements
   * Only used for non-adaptive modes (square, portrait, landscape)
   */
  #applyFixedAspectRatio() {
    if (!this.#imageRatioSetting) return;

    const aspectRatio = this.#getAspectRatioValue(this.#imageRatioSetting);
    if (!aspectRatio) return;

    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    // Batch DOM operations for better performance
    requestAnimationFrame(() => {
      newCardGalleries.forEach((gallery) => {
        if (!(gallery instanceof HTMLElement)) return;
        this.#applyAspectRatioToGallery(gallery, aspectRatio);
      });
    });
  }

  /**
   * Aspect ratio values matching the theme's standardized values
   * @type {Object.<string, string>}
   */
  static ASPECT_RATIOS = {
    square: '1',
    portrait: '0.8',
    landscape: '1.778',
  };

  /**
   * Get aspect ratio value based on setting
   * @param {string} ratioSetting - The ratio setting name
   * @returns {string|null} - The aspect ratio value or null
   */
  #getAspectRatioValue(ratioSetting) {
    return PaginatedList.ASPECT_RATIOS[ratioSetting] || null;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const page = isPrevious ? Number(targetCard.dataset.page) - 1 : Number(targetCard.dataset.page) + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }
}
